import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ShoppingBag, Flower } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Merch() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(`${API}/merch/products`);
        setProducts(response.data);
      } catch (error) {
        console.error('Error fetching products:', error);
        toast.error('Failed to load products');
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const handlePurchase = async (productId) => {
    try {
      setPurchasing(productId);
      const response = await axios.post(
        `${API}/merch/checkout`,
        {
          product_id: productId,
          origin_url: window.location.origin
        },
        { withCredentials: true }
      );

      window.location.href = response.data.url;
    } catch (error) {
      console.error('Purchase error:', error);
      toast.error('Failed to process purchase. Please try again.');
      setPurchasing(null);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <nav className="bg-white/70 backdrop-blur-lg border-b border-white/20 px-6 py-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Flower size={32} weight="fill" className="text-primary" />
            <span className="text-2xl font-heading font-bold">Bloom & Bond</span>
          </div>
          <Button 
            data-testid="back-button"
            onClick={() => navigate('/dashboard')}
            variant="ghost"
            size="sm"
          >
            ← Back
          </Button>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="text-center mb-12">
            <ShoppingBag size={64} weight="fill" className="text-primary mx-auto mb-6" />
            <h1 className="text-4xl md:text-5xl font-heading font-bold mb-4">Community Merch</h1>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Show your support with exclusive Bloom & Bond merchandise. All proceeds help maintain our community.
            </p>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="text-xl font-heading">Loading products...</div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {products.map((product) => (
                <motion.div
                  key={product.product_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  whileHover={{ y: -8 }}
                  className="bg-white/80 backdrop-blur-md border border-white/20 shadow-sm rounded-2xl overflow-hidden hover:shadow-md transition-all duration-300"
                  data-testid={`product-${product.product_id}`}
                >
                  <div className="relative h-64 overflow-hidden">
                    <img 
                      src={product.image_url} 
                      alt={product.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-heading font-semibold mb-2">{product.name}</h3>
                    <p className="text-muted-foreground text-sm mb-4">{product.description}</p>
                    <div className="flex items-center justify-between">
                      <div className="text-3xl font-heading font-bold text-primary">
                        ${product.price.toFixed(2)}
                      </div>
                      <Button
                        data-testid={`buy-button-${product.product_id}`}
                        onClick={() => handlePurchase(product.product_id)}
                        disabled={purchasing === product.product_id}
                        className="rounded-full bg-primary hover:bg-primary-hover font-bold"
                      >
                        {purchasing === product.product_id ? 'Processing...' : 'Buy Now'}
                      </Button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}